// Listado/gestión admin de reservas. Filtros + acciones por reserva.

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Search, AlertCircle, Loader2, XCircle, CheckCircle2, Wallet, UserX, Trash2,
} from 'lucide-react';

import {
  listReservas, cancelarReservaAdmin, eliminarReservaAdmin, marcarPagada, marcarAsistencia,
} from '../../lib/adminApi';

const ESTADOS = ['', 'confirmada', 'pendiente_pago', 'cancelada', 'completada', 'no_asistio'];

export default function AdminReservas() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();

  const [filtros, setFiltros] = useState({
    fecha_desde: params.get('fecha_desde') || '',
    fecha_hasta: params.get('fecha_hasta') || '',
    estado: params.get('estado') || '',
    busqueda_cliente: params.get('busqueda_cliente') || '',
  });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actuando, setActuando] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await listReservas(filtros);
      setItems(r.data || []);
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { cargar(); }, [cargar]);

  const aplicar = (e) => {
    e.preventDefault();
    const next = new URLSearchParams();
    Object.entries(filtros).forEach(([k, v]) => { if (v) next.set(k, v); });
    setParams(next);
  };

  const limpiar = () => {
    setFiltros({ fecha_desde: '', fecha_hasta: '', estado: '', busqueda_cliente: '' });
    setParams({});
  };

  const setF = (k) => (e) => setFiltros((f) => ({ ...f, [k]: e.target.value }));

  const accion = async (codigo, fn, mensaje) => {
    if (mensaje && !window.confirm(mensaje)) return;
    setActuando(codigo);
    try { await fn(); cargar(); }
    catch (e) { alert(e.message || t('admin.error_generico')); }
    finally { setActuando(null); }
  };

  // Cancelación admin: con pago online pide doble confirmación y muestra el resultado del reembolso.
  const cancelarConReembolso = async (r) => {
    const codigo = r.codigo_reserva;
    const conPago = r.pago && r.pago.metodo === 'online_stripe' && r.pago.estado === 'completado';
    const importe = r.pago ? Number(r.pago.importe_euros).toFixed(2) : '0.00';
    if (conPago) {
      if (!window.confirm(t('admin.reservas.confirmar_1_reembolso', { codigo, importe }))) return;
      if (!window.confirm(t('admin.reservas.confirmar_2_reembolso', { codigo, importe }))) return;
    } else if (!window.confirm(t('admin.reservas.confirmar_1_sin_reembolso', { codigo }))) return;
    setActuando(codigo);
    try {
      const res = await cancelarReservaAdmin(codigo);
      let msg = t('admin.reservas.resultado_cancelada', { codigo });
      if (res.reembolso_pendiente) {
        msg = t(res.reembolso_iniciado ? 'admin.reservas.resultado_reembolsada' : 'admin.reservas.resultado_reembolso_fallo', { codigo, importe });
      }
      alert(msg);
      cargar();
    } catch (e) { alert(e.message || t('admin.error_generico')); }
    finally { setActuando(null); }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>{t('admin.reservas.titulo')}</h1>
          <p className="admin-page-sub">{t('admin.reservas.sub')}</p>
        </div>
      </header>

      <form className="admin-filtros" onSubmit={aplicar}>
        <div className="modal-campo">
          <label>{t('admin.reservas.filtro.desde')}</label>
          <input type="date" value={filtros.fecha_desde} onChange={setF('fecha_desde')} />
        </div>
        <div className="modal-campo">
          <label>{t('admin.reservas.filtro.hasta')}</label>
          <input type="date" value={filtros.fecha_hasta} onChange={setF('fecha_hasta')} />
        </div>
        <div className="modal-campo">
          <label>{t('admin.reservas.filtro.estado')}</label>
          <select value={filtros.estado} onChange={setF('estado')}>
            {ESTADOS.map((e) => <option key={e} value={e}>{e || t('admin.todos')}</option>)}
          </select>
        </div>
        <div className="modal-campo full">
          <label>{t('admin.reservas.filtro.busqueda')}</label>
          <input
            type="text"
            value={filtros.busqueda_cliente}
            onChange={setF('busqueda_cliente')}
            placeholder={t('admin.reservas.filtro.busqueda_ph')}
          />
        </div>
        <div className="admin-filtros-acciones">
          <button type="submit" className="btn-primary"><Search size={16} /> {t('admin.aplicar')}</button>
          <button type="button" className="btn-secondary" onClick={limpiar}>{t('admin.limpiar')}</button>
        </div>
      </form>

      {loading && <div className="admin-loading"><Loader2 size={24} className="spin" /></div>}
      {error && <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>}

      {!loading && !error && (
        <>
          <p className="admin-total">{t('admin.reservas.total', { n: items.length })}</p>
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('admin.tabla.codigo')}</th>
                <th>{t('admin.tabla.fecha_hora')}</th>
                <th>{t('admin.tabla.clase')}</th>
                <th>{t('admin.tabla.titular')}</th>
                <th>{t('admin.tabla.pago')}</th>
                <th>{t('admin.tabla.estado')}</th>
                <th>{t('admin.tabla.acciones')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const titular = r.usuario_id
                  ? `${r.usuario?.nombre || ''} ${r.usuario?.apellidos || ''}`.trim()
                  : `${r.nombre_guest} (${r.email_guest})`;
                const cancelable = r.estado === 'confirmada' || r.estado === 'pendiente_pago';
                const pagable = r.pago?.metodo === 'en_estudio' && r.pago?.estado !== 'completado';
                const marcable = r.estado === 'confirmada' || r.estado === 'pendiente_pago';
                const eliminable = r.estado === 'cancelada';
                const trabajando = actuando === r.codigo_reserva;

                return (
                  <tr key={r.id}>
                    <td><code>{r.codigo_reserva}</code></td>
                    <td>{r.fecha_clase} · {r.horario?.hora_inicio?.slice(0, 5)}</td>
                    <td>
                      {r.horario?.clase_tipo?.nombre_es}<br />
                      <small>{r.horario?.profesor?.nombre_publico}</small>
                    </td>
                    <td>{titular}</td>
                    <td>
                      {r.pago ? (
                        <>
                          {r.pago.metodo}<br />
                          <small className={'pago-' + r.pago.estado}>{r.pago.estado}</small><br />
                          <small>{r.pago.importe_euros} €</small>
                        </>
                      ) : '—'}
                    </td>
                    <td><span className={'estado-pill estado-' + r.estado}>{r.estado}</span></td>
                    <td className="admin-acciones">
                      {trabajando && <Loader2 size={16} className="spin" />}
                      {cancelable && !trabajando && (
                        <button type="button" title={t('admin.reservas.accion.cancelar')} onClick={() => cancelarConReembolso(r)}><XCircle size={16} /></button>
                      )}
                      {eliminable && !trabajando && (
                        <button type="button" title={t('admin.reservas.accion.eliminar')} onClick={() => accion(
                          r.codigo_reserva,
                          () => eliminarReservaAdmin(r.codigo_reserva),
                          t('admin.reservas.confirmar_eliminar', { codigo: r.codigo_reserva }),
                        )}><Trash2 size={16} /></button>
                      )}
                      {pagable && !trabajando && (
                        <button type="button" title={t('admin.reservas.accion.pagada')} onClick={() => accion(
                          r.codigo_reserva,
                          () => marcarPagada(r.codigo_reserva),
                        )}><Wallet size={16} /></button>
                      )}
                      {marcable && !trabajando && (
                        <>
                          <button type="button" title={t('admin.reservas.accion.completada')} onClick={() => accion(
                            r.codigo_reserva,
                            () => marcarAsistencia(r.codigo_reserva, 'completada'),
                          )}><CheckCircle2 size={16} /></button>
                          <button type="button" title={t('admin.reservas.accion.no_asistio')} onClick={() => accion(
                            r.codigo_reserva,
                            () => marcarAsistencia(r.codigo_reserva, 'no_asistio'),
                          )}><UserX size={16} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
